import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';

// Import modules
import { auth } from './modules/auth';
import { notifications } from './modules/notifications';
import { user } from './modules/user';
import { workspace } from './modules/workspace';
import app from './modules/app';

/**
 * Vuex Store Configuration
 *
 * This is the main store file that combines all modules and configures
 * persistence and plugins. It follows a modular pattern to keep the store
 * organized as the application grows.
 */
export default createStore({
    /**
     * Root state for application-wide data
     */
    state: {
        appVersion: process.env.VUE_APP_VERSION || '1.0.0',
        appName: 'Playtime',
        isAppLoading: false
    },

    /**
     * Root mutations for modifying root state
     */
    mutations: {
        SET_LOADING(state, isLoading) {
            state.isAppLoading = isLoading;
        }
    },

    /**
     * Root actions for complex operations
     */
    actions: {
        /**
         * Set application loading state
         */
        setAppLoading({ commit }, isLoading) {
            commit('SET_LOADING', isLoading);
        },

        /**
         * Initialize application state
         * Called on app startup to restore and initialize all modules
         */
        async initializeApp({ dispatch, rootGetters }) {
            try {
                // Set app loading
                dispatch('setAppLoading', true);

                // Only initialize if user is logged in
                if (rootGetters['auth/isLoggedIn']) {
                    // Initialize workspace state (this will also load user workspaces)
                    await dispatch('workspace/initialize');

                    // Initialize other modules that depend on workspace
                    // Add other initialization calls here as needed
                }

            } catch (error) {
                console.error('Error initializing application:', error);
            } finally {
                dispatch('setAppLoading', false);
            }
        },

        /**
         * Reset all application state
         * Called on logout to clean up all state
         */
        async resetAppState({ dispatch }) {
            // Reset all module states
            await dispatch('auth/logout');
            dispatch('user/resetUserState');
            dispatch('workspace/resetWorkspaceState');
            dispatch('notifications/clearAll');
        }
    },

    /**
     * Root getters for derived state
     */
    getters: {
        appVersion: state => state.appVersion,
        appName: state => state.appName,
        isAppLoading: state => state.isAppLoading,

        /**
         * Check if application is fully initialized
         */
        isAppInitialized: (state, getters, rootState, rootGetters) => {
            return !state.isAppLoading && (
                !rootGetters['auth/isLoggedIn'] ||
                rootGetters['workspace/hasWorkspaces']
            );
        }
    },

    /**
     * Modules for organizing state by feature
     */
    modules: {
        auth,
        notifications,
        user,
        workspace,
        app
    },

    /**
     * Plugins for extending store functionality
     */
    plugins: [
        // Persist selected state to localStorage
        createPersistedState({
            // Only persist auth state and current workspace (excluding sensitive data)
            paths: ['auth.tokens', 'auth.user', 'workspace.currentWorkspace'],

            // Custom storage options
            storage: {
                getItem: key => localStorage.getItem(key),
                setItem: (key, value) => localStorage.setItem(key, value),
                removeItem: key => localStorage.removeItem(key)
            },

            // Custom serialization to avoid circular references
            serialize: (data) => {
                return JSON.stringify(data, (key, value) => {
                    // Don't persist error objects in storage
                    if (key === 'error' && value instanceof Error) {
                        return undefined;
                    }
                    // Don't persist loading states
                    if (key === 'loading' && typeof value === 'object') {
                        return undefined;
                    }
                    return value;
                });
            },

            // Regular parsing is fine since we sanitized during serialization
            deserialize: (data) => JSON.parse(data),

            // Only persist when state changes are significant
            filter: (mutation) => {
                const persistedMutations = [
                    'auth/loginSuccess',
                    'auth/logout',
                    'auth/refreshTokenSuccess',
                    'workspace/SET_CURRENT_WORKSPACE'
                ];
                return persistedMutations.includes(mutation.type);
            }
        })
    ],

    // Enable strict mode in development
    strict: process.env.NODE_ENV !== 'production'
});